import React, { useCallback, useMemo } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import {
  Box,
  useTheme,
  Typography,
  Button,
  Avatar,
  ToggleButtonGroup,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded"
import {
  CURRENT_EVENT_FILTER_VALUE,
  Q_PROMOTION_GROUP,
  Q_VIEW_MODE,
  SHOP_PROMOTION_VIEW_MODE,
  UPCOMING_EVENT_FILTER_VALUE,
} from "constant"
import { getDurationDate } from "utils/date"
import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { usePromotionDetail } from "hooks/promotion/usePromotionDetail"
import { usePromotion } from "hooks/promotion/usePromotion"
import { useMobileUrl } from "hooks/useMobileUrl"

import { PageCard, PageCardContent } from "components/card"
import { NavigationControl } from "components/kioskHorizontal/navigation"
import { QRCode } from "components/QRCode"
import { PromotionCard } from "components/domains/promotions"
import { LocationIcon, PercentIcon, ShopIcon } from "components/icons"
import { Carousel, CarouselItem } from "components/Carousel"
import { EmptyData } from "components/EmptyData"
import { InfoListItem } from "components/ListItem"
import { PromotionDetailSidebarSkeleton } from "components/domains/promotions/kiosk-h/PromotionDetailSidebarSkeleton"
import { MotionDiv } from "components/motions"
import { CategoryFilterChip } from "components/CategoryFilterBar"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"

//TODO: remove this component when refactor setting initial active event group to a query string to reduce the number of re-renders.
const SidebarMenuContent = ({ AppNavigation = DefaultAppNavigation } = {}) => {
  const theme = useTheme()
  const { styleOverrides: bannerStyleOverrides = {} } =
    theme.components[`AppBanner`] || {}
  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}

  const { root: bannerRoot } = bannerStyleOverrides
  const { wrapper, body, footer } = sideBarStyleOverrides

  return (
    <MotionDiv
      sx={wrapper}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <Box sx={bannerRoot}></Box>
      <Box sx={body}>{AppNavigation && <AppNavigation />}</Box>
      <Box sx={footer}>
        <NavigationControl />
      </Box>
    </MotionDiv>
  )
}

export const PromotionDetailKioskHPage = ({ Layout = Box }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const [searchParams] = useSearchParams()
  const isShopPromotionViewMode =
    searchParams.get(Q_VIEW_MODE) === SHOP_PROMOTION_VIEW_MODE
  const { promotionId } = params
  const theme = useTheme()

  const { groupPromotions, activeGroupPromotion, handleChange } = usePromotion()
  const { promotion, navigateToLocation, relatedShopPromotions } =
    usePromotionDetail(promotionId, {
      useReactQuery: false,
    })

  const { mobileUrl } = useMobileUrl()

  const promotionToRender = isShopPromotionViewMode
    ? relatedShopPromotions
    : groupPromotions[activeGroupPromotion]

  const hasEmptyList =
    promotion && promotionToRender && promotionToRender.length === 0
  const slidePerPage = 3
  const carouselType =
    promotionToRender && promotionToRender.length < slidePerPage
      ? "slide"
      : "loop"
  const label = !isShopPromotionViewMode
    ? `${activeGroupPromotion}-promotion`
    : "shop-promotion"

  const {
    locationButton,
    titleText,
    descriptionText,
    qrCode,
    detailContainer,
    infoListItem,
    locationButtonContainer,
    infoContainer,
    listItemContainer,
    qrcodeText,
    headerContainer,
    avatarRoot,
    titleContainer,
    locationTitle,
    locationSubTitle,
    sidebarContent,
    titleConnectorIcon,
  } = theme.components[`PromotionDetailPage`]?.styleOverrides || {}
  const { promotionCarouselOption } =
    theme.components[`PromotionDetailPage`]?.defaultProps || {}

  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}
  const { wrapper, body, footer } = sideBarStyleOverrides

  const { styleOverrides: PromotionPageStyle } =
    theme.components?.[`PromotionPage`] || {}
  const {
    carouselContainer,
    carousel,
    promotionCard,
    pageCardContent,
    emptyDataWrapper,
    promotionGroupSelectorButton,
    pageCard,
  } = PromotionPageStyle

  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarTodayOutlinedIcon />,
    [theme]
  )

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.promotions) || <PercentIcon />,
    [theme]
  )

  const connectorIcon = useMemo(
    () =>
      getIcon(theme?.assets?.promotions?.connectorIcon, titleConnectorIcon) || (
        <KeyboardArrowRightRoundedIcon sx={titleConnectorIcon} />
      ),
    [theme, titleConnectorIcon]
  )

  const occupantDetail = useMemo(() => {
    return promotion?.occupantDetail
  }, [promotion])

  const handleOnReady = useCallback(
    (splide) => {
      const slideIndex = promotionToRender.findIndex(
        (promotion) => promotion?.id === promotionId
      )
      splide.go(slideIndex)
    },
    [promotionToRender, promotionId]
  )

  const handleClickSlide = useCallback(
    (splide, { index, slide, ...rest }, _event) => {
      const [activeSlideIndex] = slide.getAttribute("aria-label")?.split(" ")
      if (!activeSlideIndex) return
      splide.go(+activeSlideIndex - 1)
    },
    []
  )

  const handleOnMoved = useCallback(
    (splide, index, prev, dest) => {
      const slides = splide?.root.querySelectorAll(
        ".splide__slide:not(.splide__slide--clone)"
      )
      const activeSlide = slides[index]
      const path = activeSlide?.getAttribute("link")
      navigate(path, {
        replace: true,
      })
    },
    [navigate]
  )

  const handleOnUpdated = useCallback(
    (splide, options) => {
      const isShopPromotionCarousel = options?.label === "shop-promotion"

      if (isShopPromotionCarousel) return

      // Navigate to the active slide when switching promotion groups, unless on the shop promotion page.
      const slides = splide?.root.querySelectorAll(
        ".splide__slide:not(.splide__slide--clone)"
      )
      const activeSlide = slides[splide?.index]
      const path = activeSlide?.getAttribute("link")

      if (path)
        navigate(path, {
          replace: true,
        })
    },
    [navigate]
  )

  return (
    <Layout
      aside={
        <PageCard>
          {hasEmptyList ? (
            <SidebarMenuContent />
          ) : (
            <MotionDiv
              sx={deepmerge(wrapper, sidebarContent)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              <Box sx={body}>
                {promotion ? (
                  <Box sx={detailContainer}>
                    <Typography variant="h6" sx={titleText}>
                      {t(promotion.name)}
                    </Typography>
                    {promotion.descriptionText && (
                      <Typography
                        sx={descriptionText}
                        variant="body1"
                        dangerouslySetInnerHTML={{
                          __html: promotion.descriptionText,
                        }}
                      />
                    )}
                    <Box sx={infoContainer}>
                      {mobileUrl && (
                        <Box>
                          <QRCode sx={qrCode} url={mobileUrl} />
                          <Typography
                            variant="subtitle2"
                            component="p"
                            sx={qrcodeText}
                          >
                            {t("Scan Here")}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            component="p"
                            sx={qrcodeText}
                          >
                            {t("For Mobile")}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={listItemContainer}>
                        <InfoListItem
                          sx={infoListItem}
                          icon={calendarIcon}
                          primary={getDurationDate(
                            promotion.startDate,
                            promotion.endDate
                          )}
                          secondary={promotion.openingText}
                        />
                        {promotion.hasLocation && (
                          <InfoListItem
                            sx={infoListItem}
                            icon={locationIcon}
                            primary={t(promotion.location)}
                            secondary={t("Level Text", {
                              level: t(promotion.level),
                            })}
                          />
                        )}
                      </Box>
                    </Box>

                    {promotion.hasLocation && (
                      <Box sx={locationButtonContainer}>
                        <Button
                          sx={locationButton}
                          variant="contained"
                          onClick={navigateToLocation}
                        >
                          {t("View Location")}
                        </Button>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <PromotionDetailSidebarSkeleton />
                )}
              </Box>
              <Box sx={footer}>
                <NavigationControl />
              </Box>
            </MotionDiv>
          )}
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent sx={pageCardContent}>
          {isShopPromotionViewMode && occupantDetail && (
            <Box sx={headerContainer}>
              <Avatar src={occupantDetail.logo} sx={avatarRoot} variant="logo">
                {getIcon(
                  {
                    icon_path: occupantDetail.categoryIconPath,
                    image: occupantDetail.categoryIconImage,
                  },
                  { fontSize: "inherit" },
                  {
                    imageIconProps: {
                      solidColorFilterId: "shopPromotion_avatar_filter",
                    },
                  }
                ) || <ShopIcon fontSize="inherit" />}
              </Avatar>
              <Box sx={titleContainer}>
                <Typography sx={locationTitle}>
                  {t(occupantDetail.name)}
                </Typography>
                {connectorIcon}
                <Typography sx={locationSubTitle}>{t("Promotion")}</Typography>
              </Box>
            </Box>
          )}
          <MotionDiv
            sx={carouselContainer}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
          >
            {promotionToRender && promotionToRender.length > 0 ? (
              <Carousel
                onClick={handleClickSlide}
                options={{
                  type: carouselType,
                  perPage: slidePerPage,
                  pagination: true,
                  gap: "0",
                  focus: "center",
                  label,
                  ...promotionCarouselOption,
                }}
                onUpdated={handleOnUpdated}
                onReady={handleOnReady}
                onMoved={handleOnMoved}
                sx={carousel}
              >
                {promotionToRender.map((promotion) => {
                  const sponsoredId = promotion?.properties?.sponsoredId || ""
                  // NOTE: Shop promotion view mode does not have an event group filter.
                  const queryString = isShopPromotionViewMode
                    ? `?${Q_VIEW_MODE}=${SHOP_PROMOTION_VIEW_MODE}`
                    : `?${Q_PROMOTION_GROUP}=${activeGroupPromotion}`

                  return (
                    <CarouselItem
                      link={`/promotions/${promotion.id}${queryString}`}
                      key={`${sponsoredId}${promotion.id}`}
                    >
                      <PromotionCard promotion={promotion} sx={promotionCard} />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            ) : (
              <Box sx={emptyDataWrapper}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("Coming soon")}
                  secondary={t("Check Back Soon")}
                />
              </Box>
            )}
          </MotionDiv>
          {!isShopPromotionViewMode && (
            <Box sx={promotionGroupSelectorButton}>
              <ToggleButtonGroup
                value={activeGroupPromotion}
                onChange={handleChange}
                exclusive
              >
                <CategoryFilterChip value={CURRENT_EVENT_FILTER_VALUE}>
                  {t("Current Promotions")}
                </CategoryFilterChip>
                <CategoryFilterChip value={UPCOMING_EVENT_FILTER_VALUE}>
                  {t("Upcoming Promotions")}
                </CategoryFilterChip>
              </ToggleButtonGroup>
            </Box>
          )}
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
